import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, GraduationCap, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AdminShell } from "./AdminShell";
import { PageHeader, StatusBadge, StatusMenu } from "./parts";

const EMPTY = { identifiant: "", email: "", password: "", first_name: "", last_name: "", school_id: "" };

function TeacherDialog({ open, onOpenChange, teacher, schools, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!teacher;
  useEffect(() => {
    setForm(teacher
      ? { identifiant: teacher.identifiant, email: teacher.email || "", password: "", first_name: teacher.first_name, last_name: teacher.last_name, school_id: teacher.school_id || "" }
      : EMPTY);
  }, [teacher, open]);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Nom et prénom obligatoires.");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/users/${teacher.id}`, {
          first_name: form.first_name, last_name: form.last_name, email: form.email, school_id: form.school_id || undefined,
        });
      } else {
        await api.post("/admin/teachers", {
          identifiant: form.identifiant, email: form.email || undefined, password: form.password,
          first_name: form.first_name, last_name: form.last_name, school_id: form.school_id || undefined,
        });
      }
      toast.success(editing ? "Enseignant modifié." : "Enseignant créé.");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="teacher-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier l'enseignant" : "Nouvel enseignant"}</DialogTitle>
          <DialogDescription>Gérez le compte enseignant et son rattachement à une école.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Prénom *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="teacher-firstname-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Nom *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="teacher-lastname-input" />
            </div>
          </div>
          {!editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Identifiant *</Label>
                <Input value={form.identifiant} onChange={(e) => setForm({ ...form, identifiant: e.target.value })} data-testid="teacher-identifiant-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Mot de passe *</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="teacher-password-input" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>École</Label>
            <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
              <SelectTrigger data-testid="teacher-school-select"><SelectValue placeholder="Sélectionner une école" /></SelectTrigger>
              <SelectContent>
                {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="teacher-save-btn">
            {saving && <Loader2 className="mr-2 animate-spin" size={16} />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState(null);
  const [schools, setSchools] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempPw, setTempPw] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([api.get("/admin/users?role=teacher"), api.get("/admin/schools")]);
      setTeachers(t.data);
      setSchools(s.data);
    } catch {
      toast.error("Impossible de charger les enseignants.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const schoolName = (id) => schools.find((s) => s.id === id)?.name || "—";

  const changeStatus = async (u, status) => {
    try {
      await api.post(`/admin/users/${u.id}/status`, { status });
      toast.success(status === "supprimé" ? "Compte supprimé (restaurable 30 jours)." : "Statut mis à jour.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetPassword = async (u) => {
    try {
      const res = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPw({ name: `${u.first_name} ${u.last_name}`, pw: res.data.temp_password });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminShell>
      <PageHeader
        title="Enseignants"
        subtitle="Comptes enseignants de la plateforme."
        action={
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-teacher-btn">
            <Plus size={16} className="mr-1.5" /> Ajouter un enseignant
          </Button>
        }
      />
      {teachers === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="teachers-table">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Enseignant</th>
                <th className="px-5 py-3">Identifiant</th>
                <th className="px-5 py-3">École</th>
                <th className="px-5 py-3 text-center">Classes</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((u) => (
                <tr key={u.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40" data-testid={`teacher-row-${u.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">
                    <div className="flex items-center gap-2"><GraduationCap size={15} className="text-slate-400" />{u.first_name} {u.last_name}</div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{u.identifiant}</td>
                  <td className="px-5 py-3.5 text-slate-600">{schoolName(u.school_id)}</td>
                  <td className="px-5 py-3.5 text-center text-slate-600">{u.class_count}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={u.status} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => { setEditing(u); setDialogOpen(true); }} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-900" data-testid={`edit-teacher-${u.id}`}><Pencil size={16} /></button>
                      <StatusMenu
                        current={u.status}
                        onChange={(st) => changeStatus(u, st)}
                        testid={`teacher-menu-${u.id}`}
                        extra={<DropdownMenuItem onClick={() => resetPassword(u)} data-testid={`reset-teacher-${u.id}`}><KeyRound size={15} className="mr-2" /> Réinitialiser le mot de passe</DropdownMenuItem>}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TeacherDialog open={dialogOpen} onOpenChange={setDialogOpen} teacher={editing} schools={schools} onSaved={load} />

      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau mot de passe</DialogTitle>
            <DialogDescription>Communiquez ce mot de passe temporaire à {tempPw?.name}.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <code className="flex-1 text-lg font-semibold tracking-wide text-slate-900" data-testid="temp-password-value">{tempPw?.pw}</code>
            <button onClick={() => { navigator.clipboard.writeText(tempPw?.pw || ""); toast.success("Copié."); }} className="rounded-lg p-1.5 text-slate-500 hover:bg-white"><Copy size={16} /></button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
